import { lazy, useMemo } from 'react';
import type { ReactNode } from 'react';
import { SyncConfigurationEntityType } from '../sync-configuration-page-content/syncConfigurationPageContent.model';
import { CoreTypography, KebabMenu, CoreTypographyVariant, MultiActions } from '@ingenico/react-ui';
import { ExposedSyncConfigurationStoreModal } from '../exposed-sync-configuration-store-modal/ExposedSyncConfigurationStoreModal.ctrl';
import { ExposedSyncConfigurationMerchantModal } from '../exposed-sync-configuration-merchant-modal/ExposedSyncConfigurationMerchantModal.ctrl';
import { isEmpty } from 'lodash-es';
import styles from './SyncConfigurationButton.module.scss';
import { useTranslation } from 'react-i18next';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useSyncConfigurationButtonVM } from './useSyncConfigurationButton.vm';

export interface SyncConfigurationButtonProps {
  readonly selectedEntity?: SyncConfigurationEntityType | null;
  readonly id?: string | string[];
}

export function SyncConfigurationButton({ selectedEntity, id }: SyncConfigurationButtonProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);

  const {
    kebabOptions,
    setKebabMenuOptions,
    syncConfigurationModalComponent,
    handleSyncConfiguration,
    shouldDisplayKebabMenu,
  } = useSyncConfigurationButtonVM({ id, selectedEntity });

  const setterComponent = useMemo(() => {
    switch (selectedEntity) {
      case SyncConfigurationEntityType.Store:
        return <ExposedSyncConfigurationStoreModal onStateChange={setKebabMenuOptions} id={id as string} />;
      case SyncConfigurationEntityType.Merchant:
        return <ExposedSyncConfigurationMerchantModal onStateChange={setKebabMenuOptions} id={id as string} />;
      default:
        return null;
    }
  }, [selectedEntity, setKebabMenuOptions, id]);

  return (
    <>
      <div className={styles.container}>
        {setterComponent}
        <div className={styles.actions__container}>
          {shouldDisplayKebabMenu && <KebabMenu disabled={isEmpty(id)} options={kebabOptions} />}
          {selectedEntity === SyncConfigurationEntityType.Device && Array.isArray(id) && id.length > 1 && (
            <MultiActions
              leftSlot={{
                label: t('title.multiActions.multipleSelection', {
                  ns: ALL_LNGS_NAMESPACES.COMMON,
                  quantitySelectedElements: Array.isArray(id) ? id.length : 0,
                }),
              }}
              rightSlot={[
                {
                  onClick: handleSyncConfiguration,
                  label: t('button.syncConfigurationOpenModal', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
                  key: 'sync-configuration',
                  icon: lazy(async () => import('@mui/icons-material/Sync')),
                },
              ]}
              show
            />
          )}
        </div>
        {isEmpty(id) && (
          <CoreTypography variant={CoreTypographyVariant.Label01Regular}>
            Insert an Id to enable the button
          </CoreTypography>
        )}
      </div>
      {syncConfigurationModalComponent}
    </>
  );
}
